// src/pages/finance-portal/requisitions/Requisitions.js
//
// Purchase Requisitions list

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:     { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED: { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:  { bg: '#dcfce7', color: '#166534' },
    REJECTED:  { bg: '#fecaca', color: '#991b1b' },
    CONVERTED: { bg: '#e0e7ff', color: '#4338ca' },
    CANCELLED: { bg: '#f1f5f9', color: '#94a3b8' },
};

const URGENCY_COLORS = {
    LOW: '#94a3b8', NORMAL: '#64748b', HIGH: '#f59e0b', URGENT: '#ef4444',
};

const STATUS_TABS = ['ALL', 'DRAFT', 'SUBMITTED', 'APPROVED', 'CONVERTED', 'REJECTED'];

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 0,
    }).format(val || 0);
}

export default function Requisitions() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    const fetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const status = STATUS_TABS[statusTab];
            if (status !== 'ALL') params.status = status;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/requisitions', { params });
            if (res.data.success) {
                setRows(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetch(); }, [fetch]);

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                        textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <RequestQuoteIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Purchase Requisitions
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)',
                                              fontSize: '0.9rem', mt: 0.25 }}>
                                Request-to-procure with approval workflow
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => navigate('/finance-portal/requisitions/new')}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                  bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                        New Requisition
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Search by number / dept / justification..."
                               value={search}
                               onChange={e => { setSearch(e.target.value); setPage(0); }}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start">
                                       <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                   </InputAdornment>,
                                   sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                               }}
                               sx={{ minWidth: { xs: '100%', sm: 360 } }} />
                </Box>

                <Tabs value={statusTab} onChange={(_, v) => { setStatusTab(v); setPage(0); }}
                      sx={{ mb: 2,
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none',
                                                fontSize: '0.85rem', color: '#64748b', minHeight: 40, py: 1 },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' } }}>
                    {STATUS_TABS.map(t => <Tab key={t} label={t.replace('_', ' ')} />)}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Req #', 'Department', 'Urgency', 'Status', 'Lines',
                                      'Estimated Total', 'Needed By', 'Created'].map(h =>
                                        <TableCell key={h} sx={labelCell}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1,2,3].map(i => (
                                        <TableRow key={i}><TableCell colSpan={8}><Skeleton height={36} /></TableCell></TableRow>
                                    ))
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <RequestQuoteIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                No requisitions found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(r => {
                                    const sStyle = STATUS_COLORS[r.status] || STATUS_COLORS.DRAFT;
                                    return (
                                        <TableRow key={r.id} hover
                                                  onClick={() => navigate(`/finance-portal/requisitions/${r.id}`)}
                                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {r.requisition_number}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{r.department || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={r.urgency} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: '#f8fafc',
                                                            color: URGENCY_COLORS[r.urgency] || '#64748b',
                                                            border: `1px solid ${URGENCY_COLORS[r.urgency] || '#cbd5e1'}` }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={r.status} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: sStyle.bg, color: sStyle.color }} />
                                            </TableCell>
                                            <TableCell sx={dataCell}>{r.line_count}</TableCell>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {fmtMoney(r.estimated_total, r.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {r.needed_by_date || '—'}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {r.created_at ? r.created_at.slice(0, 10) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={total} page={page}
                                     onPageChange={(_, p) => setPage(p)}
                                     rowsPerPage={rowsPerPage}
                                     onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                     sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }} />
                </Card>
            </Box>
        </Box>
    );
}
